import { supabase } from '../lib/supabase';
import { callGenerateEdgeFunction } from './edgeFunctionClient';

export interface SuggestedChange {
  id: string;
  section: string;
  original: string;
  suggested: string;
  reason: string;
  importance: 'critical' | 'high' | 'medium' | 'low';
  category: string;
}

export interface DocumentReviewResult {
  complianceScore: number;
  overallAssessment: string;
  keyStrengths: string[];
  criticalIssues: string[];
  suggestedChanges: SuggestedChange[];
  rentalRightsBillCompliance: {
    compliant: boolean;
    violations: string[];
    improvements: string[];
  };
}

const REVIEW_SYSTEM_PROMPT = `You are an expert UK property law solicitor specializing in the Rental Rights Bill 2025 and tenancy law. Your role is to review tenancy agreements and provide comprehensive compliance analysis.

Key focus areas for the Rental Rights Bill 2025:
1. Prohibition of fixed-term tenancies (all tenancies must be periodic)
2. Rent cannot exceed advertised amount (anti-bidding war provisions)
3. Enhanced tenant protections and rights
4. Landlord obligation requirements
5. Grounds for possession and termination procedures
6. Rent increase limitations and procedures
7. Property condition and safety standards
8. Deposit protection compliance
9. Right to rent checks
10. Anti-discrimination provisions

Provide detailed, actionable feedback with specific clause references and legal justification.`;

export const reviewDocument = async (
  documentContent: string,
  documentType: string,
  documentGenerationId?: string
): Promise<DocumentReviewResult> => {
  const reviewPrompt = `Review this ${documentType} tenancy agreement for compliance with the UK Rental Rights Bill 2025 and current tenancy law.

DOCUMENT TO REVIEW:
${documentContent}

Provide a comprehensive analysis in the following JSON format:
{
  "complianceScore": <0-100>,
  "overallAssessment": "<detailed summary of document quality and compliance>",
  "keyStrengths": ["<strength 1>", "<strength 2>", ...],
  "criticalIssues": ["<issue 1>", "<issue 2>", ...],
  "suggestedChanges": [
    {
      "id": "<unique-id>",
      "section": "<section name or clause number>",
      "original": "<original problematic text>",
      "suggested": "<improved replacement text>",
      "reason": "<detailed legal explanation>",
      "importance": "<critical|high|medium|low>",
      "category": "<Rental Rights Bill 2025|Tenant Rights|Landlord Obligations|etc>"
    }
  ],
  "rentalRightsBillCompliance": {
    "compliant": <true|false>,
    "violations": ["<violation 1>", "<violation 2>", ...],
    "improvements": ["<improvement 1>", "<improvement 2>", ...]
  }
}

Ensure all suggested changes are specific, legally accurate, and include exact replacement text that can be directly incorporated.`;

  const result = await callGenerateEdgeFunction({
    action: 'review',
    systemPrompt: REVIEW_SYSTEM_PROMPT,
    userPrompt: reviewPrompt,
    maxTokens: 8000,
    temperature: 0.3,
    provider: 'claude',
  });

  let reviewResult: DocumentReviewResult;
  try {
    const jsonMatch = result.content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No JSON found in response');
    }
    reviewResult = JSON.parse(jsonMatch[0]);
  } catch (error) {
    console.error('Failed to parse review response:', error);
    throw new Error('Failed to parse AI review response. Please try again.');
  }

  if (documentGenerationId) {
    try {
      await supabase.from('document_reviews').insert({
        document_generation_id: documentGenerationId,
        review_content: reviewResult,
        compliance_score: reviewResult.complianceScore,
        suggested_changes: reviewResult.suggestedChanges,
        applied_changes: [],
      });
    } catch (error) {
      console.error('Failed to save review to database:', error);
    }
  }

  return reviewResult;
};

export const applyChangesToDocument = (
  originalContent: string,
  changesToApply: SuggestedChange[]
): string => {
  let updatedContent = originalContent;

  const sortedChanges = [...changesToApply].sort((a, b) => {
    const importanceOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return importanceOrder[a.importance] - importanceOrder[b.importance];
  });

  for (const change of sortedChanges) {
    if (change.original && change.suggested) {
      updatedContent = updatedContent.replace(change.original, change.suggested);
    }
  }

  return updatedContent;
};

export const getDocumentReviews = async (documentGenerationId: string) => {
  const { data, error } = await supabase
    .from('document_reviews')
    .select('*')
    .eq('document_generation_id', documentGenerationId)
    .order('reviewed_at', { ascending: false });

  if (error) {
    console.error('Error fetching reviews:', error);
    return [];
  }

  return data || [];
};
